import { mergeRefreshedTracks, useAlbumTrackRefresh } from './useAlbumTrackRefresh';
import { invalidateExploreSummary } from '../../explore/exploreRefresh';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { reviewsApi } from '../../../api/reviews';
import { albumApi } from '../../../api/album';
import { adminApi } from '../../../api/admin';
import { syncRelatedAlbumsCache } from './useRelatedAlbums';
import { useSearchParams } from 'react-router-dom';

const DEFAULT_RECOMMENDATION_POSITION = 999999;

export const useAlbumDetail = (albumSource, sourceId) => {
  const queryClient = useQueryClient();
  const refetchInterval = useAlbumTrackRefresh(`${albumSource}:${sourceId}`);
  return useQuery({
    refetchInterval,
    queryKey: ['albumDetail', albumSource, sourceId],
    queryFn: () => {
      const current = queryClient.getQueryData(['albumDetail', albumSource, sourceId])?.data;
      if (current?.tracksRefreshing && current.id)
        return reviewsApi.getAlbumTrackRefresh(current.id).then((incoming) => ({
          ...incoming,
          data: mergeRefreshedTracks(
            queryClient.getQueryData(['albumDetail', albumSource, sourceId])?.data,
            incoming.data,
          ),
        }));
      return albumSource === 'local'
        ? reviewsApi.getAlbumDetail(sourceId, true)
        : reviewsApi.getAlbumDetailBySource(sourceId, albumSource, true);
    },
    select: (res) => res.data,
    enabled: !!sourceId,
  });
};

// 슬러그로 앨범 상세 조회 - 내부적으로 albumSource/sourceId를 resolve하여 기존 캐시 키와 호환
// fallback: slug가 DB에 없는 non-DB 앨범일 때 ?source_id= query param으로 전달
export const useAlbumDetailBySlug = (albumSlug) => {
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const fallbackSourceId = searchParams.get('source_id');
  const fallbackSourceType = fallbackSourceId ? 'APPLE_MUSIC' : undefined;

  // Step 1: slug → albumSource + sourceId 변환 (한 번만, 영구 캐시)
  const slugInfoQuery = useQuery({
    queryKey: ['albumSlugInfo', albumSlug, fallbackSourceId ?? null],
    queryFn: async () => {
      const res = await albumApi.getAlbumBySlug(albumSlug, {
        sourceId: fallbackSourceId,
        sourceType: fallbackSourceType,
        backgroundTracks: true,
      });
      const data = res.data;
      if (!data) throw new Error('Album not found');
      const albumSource = data.albumSource.toLowerCase();
      const sourceId = data.sourceId;
      // 기존 albumDetail 캐시 키에 데이터 사전 설정 (mutations 호환성)
      queryClient.setQueryData(['albumDetail', albumSource, sourceId], res);
      // albumReviews 캐시도 사전 설정 → slug 응답 후 별도 요청 없이 즉시 렌더링 가능
      if (data.reviews) {
        queryClient.setQueryData(['albumReviews', albumSource, sourceId], { data: data.reviews });
      }
      return { albumSource, sourceId };
    },
    enabled: !!albumSlug,
    staleTime: Infinity,
  });

  const albumSource = slugInfoQuery.data?.albumSource;
  const sourceId = slugInfoQuery.data?.sourceId;

  // Step 2: 표준 albumDetail 훅 사용 (mutations/invalidation 호환)
  const detailQuery = useAlbumDetail(albumSource, sourceId);

  return {
    ...detailQuery,
    retry: () =>
      slugInfoQuery.error || !slugInfoQuery.data ? slugInfoQuery.refetch() : detailQuery.refetch(),
    error: slugInfoQuery.error || detailQuery.error,
    albumSource,
    sourceId,
    isLoading: slugInfoQuery.isLoading || detailQuery.isLoading,
    isPending: slugInfoQuery.isPending || detailQuery.isPending,
  };
};

export const useToggleBookmark = (albumSource, sourceId) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ albumInfo, currentAlbumId }) => {
      if (albumInfo.isBookmarked) {
        await reviewsApi.deleteBookmark(currentAlbumId);
        return { itemId: currentAlbumId };
      } else {
        const response = await reviewsApi.addBookmark(sourceId, albumSource.toUpperCase());
        return response.data;
      }
    },
    onSuccess: () => {
      queryClient
        .invalidateQueries({ queryKey: ['albumDetail', albumSource, sourceId] })
        .then(() => syncRelatedAlbumsCache(queryClient, albumSource, sourceId));
      queryClient.invalidateQueries({ queryKey: ['reviewsAlbums'] });
      void invalidateExploreSummary(queryClient);
    },
  });
};

export const useToggleStarred = (albumSource, sourceId) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ albumInfo }) => {
      await albumApi.updateAlbum(albumInfo.sourceId, albumInfo.albumSource, {
        isStarred: !albumInfo.isStarred,
      });
    },
    onSuccess: () => {
      queryClient
        .invalidateQueries({ queryKey: ['albumDetail', albumSource, sourceId] })
        .then(() => syncRelatedAlbumsCache(queryClient, albumSource, sourceId));
      queryClient.invalidateQueries({ queryKey: ['reviewsAlbums'] });
      void invalidateExploreSummary(queryClient);
    },
  });
};

export const useToggleHomeAlbumFeature = (albumSource, sourceId) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ albumInfo, target, recommendationComment, recommendationCommentEn }) => {
      const hasPersistedAlbumId = Number.isFinite(Number(albumInfo.id));

      if (target === 'latest') {
        if (albumInfo.homeAlbumSection) {
          return albumApi.removeHomeLatestAlbum(albumInfo.id);
        }
        if (!hasPersistedAlbumId) {
          return albumApi.addHomeLatestAlbumBySource(albumInfo.sourceId, albumInfo.albumSource);
        }
        return albumApi.addHomeLatestAlbum(albumInfo.id);
      }
      if (target === 'recommended') {
        const isHomeRecommended =
          albumInfo.isRecommended &&
          albumInfo.homeRecommendationPosition < DEFAULT_RECOMMENDATION_POSITION;

        if (!albumInfo.isRecommended && !hasPersistedAlbumId) {
          return albumApi.createRecommendAlbum(
            albumInfo.sourceId,
            albumInfo.albumSource,
            recommendationComment,
            recommendationCommentEn,
          );
        }
        return albumApi.setHomeRecommendedAlbum(
          albumInfo.id,
          !isHomeRecommended,
          recommendationComment,
          recommendationCommentEn,
        );
      }
      throw new Error('Unsupported home album feature target');
    },
    onSuccess: () => {
      queryClient
        .invalidateQueries({ queryKey: ['albumDetail', albumSource, sourceId] })
        .then(() => syncRelatedAlbumsCache(queryClient, albumSource, sourceId));
      queryClient.invalidateQueries({ queryKey: ['reviewsAlbums'] });
      void invalidateExploreSummary(queryClient);
      queryClient.invalidateQueries({ queryKey: ['home'] });
    },
  });
};

export const useManageLatestAlbum = (albumSource, sourceId) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ albumInfo, action, displayRegion }) => {
      const hasPersistedAlbumId = Number.isFinite(Number(albumInfo?.id));

      if (action === 'register') {
        return albumApi.createAlbum(albumInfo.sourceId, albumInfo.albumSource);
      }

      if (!hasPersistedAlbumId) {
        throw new Error('앨범 정보를 찾을 수 없습니다.');
      }

      if (action === 'region') {
        return albumApi.updateLatestAlbumRegion(albumInfo.id, displayRegion);
      }

      if (action === 'hide') {
        return albumApi.hideLatestAlbum(albumInfo.id);
      }

      if (action === 'removeRecommended') {
        return albumApi.removeRecommendedAlbum(albumInfo.id);
      }

      throw new Error('지원하지 않는 앨범 관리 작업입니다.');
    },
    onSuccess: () => {
      queryClient
        .invalidateQueries({ queryKey: ['albumDetail', albumSource, sourceId] })
        .then(() => syncRelatedAlbumsCache(queryClient, albumSource, sourceId));
      queryClient.invalidateQueries({ queryKey: ['reviewsAlbums'] });
      void invalidateExploreSummary(queryClient);
      queryClient.invalidateQueries({ queryKey: ['home'] });
      queryClient.invalidateQueries({ queryKey: ['adminAlbums'] });
      queryClient.invalidateQueries({ queryKey: ['homeAlbums'] });
    },
  });
};

export const useUpdateRecommendedAlbumComment = (albumSource, sourceId) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ albumInfo, recommendationComment, recommendationCommentEn }) => {
      if (!Number.isFinite(Number(albumInfo.id))) {
        return adminApi.updateRecommendedAlbumCommentBySource(
          albumInfo.sourceId,
          albumInfo.albumSource,
          recommendationComment,
          recommendationCommentEn,
        );
      }
      return adminApi.updateRecommendedAlbumComment(
        albumInfo.id,
        recommendationComment,
        recommendationCommentEn,
      );
    },
    onSuccess: () => {
      queryClient
        .invalidateQueries({ queryKey: ['albumDetail', albumSource, sourceId] })
        .then(() => syncRelatedAlbumsCache(queryClient, albumSource, sourceId));
      queryClient.invalidateQueries({ queryKey: ['reviewsAlbums'] });
      void invalidateExploreSummary(queryClient);
      queryClient.invalidateQueries({ queryKey: ['home'] });
      queryClient.invalidateQueries({ queryKey: ['homeAlbums'] });
    },
  });
};
